import { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { SHOW_CREDIT } from '../config/features';
import axios from 'axios';
import { Button } from '../components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../components/ui/sheet';
import {
  Phone,
  Menu,
  User,
  Truck,
  CreditCard,
  Search,
  ShieldCheck,
  Users,
  Headphones,
  MessageCircle,
  Home,
  Package,
  Flame,
  Laptop,
  Building2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PHONE_NUMBER = '+507 6869-1712';
const WHATSAPP_NUMBER = '+50768691712';

export default function ShopLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [navCategories, setNavCategories] = useState([]);

  useEffect(() => {
    axios.get(`${API}/public/categories`)
      .then(r => setNavCategories(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, []);

  const navLinks = [
    { to: '/', label: 'Inicio', icon: Home },
    { to: '/marcas', label: 'Laptops por Marca', icon: Laptop },
    { to: '/productos', label: 'Productos', icon: Package },
    ...(SHOW_CREDIT ? [{ to: '/credito', label: 'Equipos a Crédito', icon: CreditCard, highlight: true }] : []),
  ];

  const trustItems = [
    { Icon: Truck,       title: 'Envíos a todo Panamá', subtitle: 'Entrega rápida y segura', mobile: true  },
    { Icon: Headphones,  title: 'Soporte 100%',          subtitle: 'Te ayudamos siempre',     mobile: true  },
    { Icon: Users,       title: '+6000 clientes felices',subtitle: 'Calidad y confianza',     mobile: false },
    { Icon: ShieldCheck, title: 'Garantía 6 meses',      subtitle: 'En todos nuestros equipos', mobile: false },
  ];

  // Trust bar only on Laptops/PCs catalog
  const showTrustBar = location.pathname === '/productos';

  // Hero on home is huge; search bar is now always visible in the header
  const isHome = location.pathname === '/';
  const showSearchBar = true;

  const handleCall = () => {
    window.location.href = `tel:${PHONE_NUMBER.replace(/[^0-9+]/g, '')}`;
  };

  const handleWhatsApp = () => {
    const message = encodeURIComponent('¡Hola! Me gustaría más información sobre sus equipos.');
    window.open(`https://wa.me/${WHATSAPP_NUMBER.replace(/[^0-9]/g, '')}?text=${message}`, '_blank');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (q) {
      navigate(`/productos?search=${encodeURIComponent(q)}`);
      setSearchQuery('');
    } else {
      navigate('/productos');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* ============== HEADER ============== */}
      <header className="sticky top-0 z-50" style={{ background: 'linear-gradient(to right, #07163d, #0d3089)' }}>
        <div className="w-full mx-auto px-3 sm:px-6 lg:px-8 2xl:px-12">
          <div className="flex items-center gap-3 sm:gap-5 lg:gap-8 h-20 sm:h-24">

            {/* Logo + Tagline */}
            <Link to="/" className="flex flex-col shrink-0" data-testid="shop-logo-link">
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}/api/settings/shop-config/image/categories/logo_white.png`}
                alt="Supertech"
                className="h-7 sm:h-9 w-auto object-contain"
                onError={(e) => {
                  e.target.src = "https://customer-assets.emergentagent.com/job_b5580767-c5cc-4654-8689-b0a9d178af0b/artifacts/ng0pf8ez_supertech%20blanco.png";
                }}
              />
              <span className="hidden sm:block text-[10px] lg:text-xs text-blue-200/80 font-medium mt-1 tracking-wide">
                Tecnología que te mueve
              </span>
            </Link>

            {/* Search bar — full width mobile, centered & compact on desktop */}
            {showSearchBar && (
              <div className="flex-1 flex lg:justify-center">
                <form
                  onSubmit={handleSearch}
                  className="w-full lg:max-w-md"
                  role="search"
                >
                  <div className="relative">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Buscar laptops, marcas, modelos..."
                      className="w-full h-11 sm:h-12 pl-4 sm:pl-5 pr-14 rounded-xl bg-white text-slate-800 placeholder:text-slate-400 text-sm sm:text-base shadow-md focus:outline-none focus:ring-2 focus:ring-blue-400/50"
                      data-testid="shop-header-search"
                    />
                    <button
                      type="submit"
                      aria-label="Buscar"
                      className="absolute right-1.5 top-1/2 -translate-y-1/2 w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-primary hover:bg-blue-700 text-white flex items-center justify-center transition-colors"
                    >
                      <Search className="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Hamburger / Menu sheet */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <button
                  className="w-11 h-11 rounded-full text-white hover:bg-white/10 flex items-center justify-center shrink-0 transition-colors"
                  aria-label="Menú"
                  data-testid="shop-header-menu"
                >
                  <Menu className="w-6 h-6" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72 sm:w-80">
                <SheetHeader className="text-left">
                  <SheetTitle>Menú</SheetTitle>
                </SheetHeader>
                <nav className="mt-6 flex flex-col gap-1.5">
                  {navLinks.map(link => {
                    const active = location.pathname === link.to;
                    return (
                      <Link
                        key={link.to}
                        to={link.to}
                        onClick={() => setMobileMenuOpen(false)}
                        className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                          link.highlight
                            ? 'bg-blue-600 text-white hover:bg-blue-500'
                            : active
                              ? 'bg-blue-50 text-blue-700'
                              : 'text-slate-700 hover:bg-slate-100'
                        }`}
                        data-testid={`menu-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <link.icon className="w-5 h-5" />
                        {link.label}
                      </Link>
                    );
                  })}

                  <div className="my-3 border-t" />

                  <button
                    onClick={() => { handleCall(); setMobileMenuOpen(false); }}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-slate-700 hover:bg-slate-100"
                  >
                    <Phone className="w-5 h-5" />
                    Llamar — {PHONE_NUMBER}
                  </button>

                  <Link
                    to="/empresas"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold text-amber-700 bg-amber-50 hover:bg-amber-100"
                    data-testid="menu-link-empresas"
                  >
                    <Building2 className="w-5 h-5" />
                    Acceso Empresas (Mayorista)
                  </Link>

                  <Link
                    to="/login"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-slate-700 hover:bg-slate-100"
                  >
                    <User className="w-5 h-5" />
                    Panel Admin
                  </Link>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* ============== CATEGORY RIBBON ============== */}
      <nav
        className="border-b shadow-sm bg-white"
        style={{ background: 'linear-gradient(to right, #061334, #0c2972)' }}
        data-testid="category-ribbon"
      >
        <div className="w-full mx-auto px-3 sm:px-6 lg:px-8 2xl:px-12">
          <ul className="flex items-center justify-center gap-1 sm:gap-2 overflow-x-auto scrollbar-hide py-2.5 sm:py-3 text-white" style={{ scrollbarWidth: 'none' }}>
            {navCategories.map(cat => (
              <li key={cat.id} className="shrink-0">
                <Link
                  to={`/productos?category=${cat.id}`}
                  className="inline-flex items-center px-3 py-1.5 rounded-md text-[12px] sm:text-sm font-medium text-blue-50 hover:bg-white/10 hover:text-white transition-colors whitespace-nowrap"
                  data-testid={`ribbon-cat-${cat.id}`}
                >
                  {cat.name}
                </Link>
              </li>
            ))}
            {navCategories.length > 0 && <li className="shrink-0 text-white/15 text-base">|</li>}
            {SHOW_CREDIT && (
            <li className="shrink-0">
              <Link
                to="/credito"
                className="relative inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-[12px] sm:text-sm font-bold text-slate-900 whitespace-nowrap shadow-sm hover:shadow-md transition-all hover:-translate-y-0.5"
                style={{ background: 'linear-gradient(135deg, #fcd34d 0%, #f59e0b 100%)' }}
                data-testid="ribbon-credit"
              >
                <CreditCard className="w-3.5 h-3.5" />
                A Crédito
                <span className="absolute -top-1 -right-1 inline-flex items-center px-1.5 py-0.5 rounded text-[8px] font-extrabold uppercase bg-rose-500 text-white shadow">
                  Nuevo
                </span>
              </Link>
            </li>
            )}
            <li className="shrink-0 text-white/15 text-base">|</li>
            <li className="shrink-0">
              <Link
                to="/productos"
                className="relative inline-flex items-center px-3 py-1.5 rounded-md text-[12px] sm:text-sm font-medium text-blue-50 hover:bg-white/10 hover:text-white transition-colors whitespace-nowrap"
                data-testid="ribbon-offers"
              >
                Ofertas
                <span className="absolute -top-1 -right-1 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[8px] font-extrabold uppercase bg-rose-500 text-white shadow">
                  <Flame className="w-2 h-2" /> Hot
                </span>
              </Link>
            </li>
            <li className="shrink-0 text-white/15 text-base">|</li>
            <li className="shrink-0">
              <button
                onClick={handleWhatsApp}
                className="inline-flex items-center px-3 py-1.5 rounded-md text-[12px] sm:text-sm font-medium text-blue-50 hover:bg-white/10 hover:text-white transition-colors whitespace-nowrap"
                data-testid="ribbon-contact"
              >
                Contacto
              </button>
            </li>
          </ul>
        </div>
      </nav>

      {/* ============== TRUST BAR (only on Laptops/PCs catalog) ============== */}
      {showTrustBar && (
        <section className="border-b bg-white" data-testid="trust-bar">
          <div className="w-full mx-auto px-3 sm:px-6 lg:px-8 2xl:px-12 py-3 sm:py-5">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-4 sm:gap-x-6 sm:gap-y-5 max-w-5xl mx-auto">
              {trustItems.map((item, i) => (
                <div
                  key={i}
                  className={`${item.mobile ? 'flex' : 'hidden lg:flex'} items-center gap-2.5 sm:gap-3 min-w-0 justify-center sm:justify-start`}
                  data-testid={`trust-item-${i}`}
                >
                  <span className="shrink-0 w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center ring-1 ring-blue-100">
                    <item.Icon className="w-4 h-4 sm:w-5 sm:h-5" strokeWidth={2.2} />
                  </span>
                  <div className="min-w-0">
                    <p className="text-xs sm:text-sm font-bold text-slate-900 leading-tight">
                      {item.title}
                    </p>
                    <p className="text-[10px] sm:text-xs text-slate-500">
                      {item.subtitle}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ============== MAIN ============== */}
      <main>
        <Outlet />
      </main>

      {/* ============== FLOATING WHATSAPP BUTTON ============== */}
      <button
        onClick={handleWhatsApp}
        aria-label="Chatear por WhatsApp"
        data-testid="floating-whatsapp"
        className="fixed bottom-5 right-5 sm:bottom-6 sm:right-6 z-50 group flex items-center justify-center w-14 h-14 rounded-full bg-[#25D366] hover:bg-[#20BD5A] text-white shadow-lg shadow-emerald-500/30 transition-all hover:scale-105"
      >
        {/* gentle pulse ring */}
        <span className="absolute inset-0 rounded-full bg-[#25D366] opacity-50 animate-ping" style={{ animationDuration: '2.4s' }} />
        <MessageCircle className="relative w-6 h-6" strokeWidth={2.4} />
        {/* Tooltip on hover (desktop only) */}
        <span className="hidden sm:flex absolute right-full mr-3 top-1/2 -translate-y-1/2 px-3 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity">
          ¿Necesitas ayuda?
        </span>
      </button>

      {/* ============== FOOTER ============== */}
      <footer className="text-white py-12 mt-16" style={{ background: 'linear-gradient(to right, #07163d, #0d3089)' }}>
        <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 2xl:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}/api/settings/shop-config/image/categories/logo_white.png`}
                alt="Supertech"
                className="h-8 mb-4"
                onError={(e) => {
                  e.target.src = "https://customer-assets.emergentagent.com/job_b5580767-c5cc-4654-8689-b0a9d178af0b/artifacts/ng0pf8ez_supertech%20blanco.png";
                }}
              />
              <p className="text-slate-300 text-sm">
                Tecnología empresarial accesible
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Enlaces</h4>
              <ul className="space-y-2 text-sm text-slate-300">
                <li><Link to="/" className="hover:text-white transition-colors">Inicio</Link></li>
                <li><Link to="/productos" className="hover:text-white transition-colors">Productos</Link></li>
                {SHOW_CREDIT && <li><Link to="/credito" className="hover:text-white transition-colors">Equipos a Crédito</Link></li>}
                <li><Link to="/empresas" className="hover:text-white transition-colors font-semibold text-amber-300">Acceso Empresas (Mayorista)</Link></li>
                <li><Link to="/login" className="hover:text-white transition-colors">Panel Admin</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contacto</h4>
              <ul className="space-y-2 text-sm text-slate-300">
                <li className="flex items-center gap-2"><Phone className="w-4 h-4" /> +507 6869-1712</li>
                <li className="flex items-center gap-2"><MessageCircle className="w-4 h-4" /> WhatsApp disponible</li>
                <li>Panamá</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/20 mt-8 pt-8 text-center text-sm text-slate-400">
            © {new Date().getFullYear()} Supertech. Todos los derechos reservados.
          </div>
        </div>
      </footer>
    </div>
  );
}
